import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import EntriesTable, {
  SortingMode,
  SortDirection,
} from "../components/EntriesTable/EntriesTable";
import { useEntries, Entry } from "@/shared/contexts/EntriesContext";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { TOAST_DURATION } from "@/shared/utils/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";

type SkippedEntry = { id: number; not_found: true };

function showRestoreToast(restoredCount: number, totalCount: number, skippedCount: number) {
  const entryWord = (n: number) => (n === 1 ? "entry" : "entries");

  if (skippedCount === 0) {
    toast.success(`Restored ${restoredCount} ${entryWord(restoredCount)}`);
    return;
  }
  if (restoredCount === 0) {
    toast.error("Couldn't restore entries", {
      duration: TOAST_DURATION.error,
      description: "Please refresh the list.",
    });
    return;
  }
  toast.success(`Restored ${restoredCount} of ${totalCount} ${entryWord(totalCount)}`, {
    description: `${skippedCount} couldn't be restored — refresh to see the latest list.`,
  });
}

type Props = {
  date: Date;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  onSelectionChange?: (count: number) => void;
  onActionInProgressChange?: (action: "restore" | null) => void;
  onSortByMatter?: () => void;
  onSortByTime?: () => void;
  filterEntries?: <T extends { application?: string }>(entries: T[]) => T[];
  filterByClientMatter?: <T extends { matter_id?: string; client?: string }>(entries: T[]) => T[];
};

export type DeletedViewHandle = {
  undoSelected: () => Promise<void>;
  isRestoring: boolean;
};

const DeletedView = forwardRef<DeletedViewHandle, Props>(function DeletedView(
  {
    date,
    dateRange,
    sortingMode,
    sortDirection,
    onSelectionChange,
    onActionInProgressChange,
    onSortByMatter,
    onSortByTime,
    filterEntries,
    filterByClientMatter,
  }: Props,
  ref,
) {
  const { deletedEntries, refreshRecords } = useEntries();
  const { makeApiRequest } = useApi();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<number[]>([]);
  const [isRestoring, setIsRestoring] = useState(false);

  const [groupedEntries, setGroupedEntries] = useState<Record<string, Entry[]>>({});

  const isInRange = useCallback(
    (d: Date) => {
      if (dateRange?.from || dateRange?.to) {
        const start = dateRange.from
          ? new Date(
              dateRange.from.getFullYear(),
              dateRange.from.getMonth(),
              dateRange.from.getDate(),
              0, 0, 0, 0,
            )
          : null;
        const end = dateRange.to
          ? new Date(
              dateRange.to.getFullYear(),
              dateRange.to.getMonth(),
              dateRange.to.getDate(),
              23, 59, 59, 999,
            )
          : null;
        if (start && end) return d >= start && d <= end;
        if (start) return d >= start;
        if (end) return d <= end;
        return true;
      }
      return (
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    },
    [date, dateRange],
  );

  const dateFilteredEntries = useMemo(
    () => deletedEntries.filter((e) => isInRange(new Date(e.date))),
    [deletedEntries, isInRange],
  );

  const filteredEntries = useMemo(
    () => {
      const afterAppFilter = filterEntries ? filterEntries(dateFilteredEntries) : dateFilteredEntries;
      return filterByClientMatter ? filterByClientMatter(afterAppFilter) : afterAppFilter;
    },
    [dateFilteredEntries, filterEntries, filterByClientMatter],
  );

  const emptyMessage = useMemo(() => {
    if (filteredEntries.length > 0) return undefined;
    if (dateFilteredEntries.length > 0) return "No deleted entries match the current filter.";
    if (dateRange?.from || dateRange?.to) return "No deleted entries for this date range.";
    if (deletedEntries.length > 0) return "No deleted entries for this date.";
    return "No deleted entries.";
  }, [
    filteredEntries.length,
    dateFilteredEntries.length,
    deletedEntries.length,
    dateRange,
  ]);

  useEffect(() => {
    const groups: Record<string, Entry[]> = {};
    const mult = sortDirection === "asc" ? 1 : -1;
    if (sortingMode === "matter") {
      const unsorted: Record<string, Entry[]> = {};
      filteredEntries.forEach((row) => {
        const key = row.matter || "Unknown";
        if (!unsorted[key]) unsorted[key] = [];
        unsorted[key].push(row);
      });
      Object.keys(unsorted)
        .sort((a, b) => mult * a.localeCompare(b))
        .forEach((key) => {
          groups[key] = unsorted[key];
        });
    } else {
      const unsorted: Record<string, Entry[]> = {};
      filteredEntries.forEach((row) => {
        let hourLabel = "";
        if (row.end_time) {
          const dateObj = new Date(row.end_time);
          const hours = dateObj.getUTCHours();
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
        const key = hourLabel || "Unknown";
        if (!unsorted[key]) unsorted[key] = [];
        unsorted[key].push(row);
      });
      const hourLabelTo24Hour = (label: string): number => {
        if (label === "Unknown") return 99;
        const match = label.match(/^(\d+):00 (AM|PM)$/);
        if (!match) return 99;
        const hour = parseInt(match[1], 10);
        const isPM = match[2] === "PM";
        if (hour === 12) return isPM ? 12 : 0;
        return isPM ? hour + 12 : hour;
      };
      Object.keys(unsorted)
        .sort((a, b) => mult * (hourLabelTo24Hour(a) - hourLabelTo24Hour(b)))
        .forEach((key) => {
          groups[key] = unsorted[key];
        });
    }
    setGroupedEntries(groups);
  }, [filteredEntries, sortingMode, sortDirection]);

  const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
  const lastClickedId = useRef<number | null>(null);
  const relevantEntriesCount = filteredEntries.length;
  const selectAllChecked =
    checkedRows.size > 0 && checkedRows.size === relevantEntriesCount;
  const selectAllIndeterminate =
    checkedRows.size > 0 && checkedRows.size < relevantEntriesCount;

  const flatEntryIds = useMemo(() => {
    const ids: number[] = [];
    for (const key of Object.keys(groupedEntries)) {
      for (const entry of groupedEntries[key]) {
        ids.push(entry.id);
      }
    }
    return ids;
  }, [groupedEntries]);

  const onToggleSelectAll = useCallback(() => {
    if (selectAllChecked || selectAllIndeterminate) {
      setCheckedRows(new Set());
    } else {
      setCheckedRows(new Set(filteredEntries.map((e) => e.id)));
    }
  }, [selectAllChecked, selectAllIndeterminate, filteredEntries]);

  const onToggleRow = useCallback(
    (id: number, e: React.MouseEvent) => {
      const hasCtrl = e.ctrlKey || e.metaKey;

      if (e.shiftKey && lastClickedId.current !== null) {
        const from = flatEntryIds.indexOf(lastClickedId.current);
        const to = flatEntryIds.indexOf(id);
        if (from !== -1 && to !== -1) {
          const start = Math.min(from, to);
          const end = Math.max(from, to);
          const rangeIds = flatEntryIds.slice(start, end + 1);
          if (hasCtrl) {
            setCheckedRows((prev) => {
              const next = new Set(prev);
              for (const rid of rangeIds) next.add(rid);
              return next;
            });
          } else {
            setCheckedRows(new Set(rangeIds));
          }
          return;
        }
      }

      setCheckedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      lastClickedId.current = id;
    },
    [flatEntryIds],
  );

  const togglePlain = useCallback((id: number) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastClickedId.current = id;
  }, []);

  useEffect(() => {
    onSelectionChange?.(checkedRows.size);
  }, [checkedRows, onSelectionChange]);

  const performRestore = useCallback(
    async (ids: number[]) => {
      setIsRestoring(true);
      onActionInProgressChange?.("restore");
      try {
        const resp = await makeApiRequest({
          endpoint: "/records/restore",
          method: "POST",
          body: { ids },
        });
        if (resp.error) throw new Error(String(resp.error));

        const { restored = [], skipped = [] } = (resp.data ?? {}) as {
          restored?: number[];
          skipped?: SkippedEntry[];
        };
        showRestoreToast(restored.length, ids.length, skipped.length);
        setCheckedRows(new Set());
        await refreshRecords();
      } catch (e) {
        if (e instanceof Error) {
          logger.captureException(e, { action: "restore", entryCount: ids.length });
        }
        toast.error("Failed to restore entries", {
          duration: TOAST_DURATION.error,
          description: e instanceof Error ? e.message : undefined,
        });
      } finally {
        setIsRestoring(false);
        onActionInProgressChange?.(null);
      }
    },
    [makeApiRequest, onActionInProgressChange, refreshRecords],
  );

  const undoSelected = useCallback(async () => {
    if (checkedRows.size === 0) {
      toast.error("Please select entries to restore");
      return;
    }
    const ids = filteredEntries
      .filter((e) => checkedRows.has(e.id))
      .map((e) => e.id);
    if (ids.length === 0) return;
    setPendingIds(ids);
    setConfirmOpen(true);
  }, [checkedRows, filteredEntries]);

  useImperativeHandle(
    ref,
    () => ({ undoSelected, isRestoring }),
    [undoSelected, isRestoring],
  );

  return (
    <>
      <EntriesTable
        entries={filteredEntries}
        groupedEntries={groupedEntries}
        columnStorageKey="entries-table:deleted"
        checkedRows={checkedRows}
        selectAllChecked={selectAllChecked}
        selectAllIndeterminate={selectAllIndeterminate}
        onToggleSelectAll={onToggleSelectAll}
        onToggleRow={onToggleRow}
        onRowClick={(entry) => togglePlain(entry.id)}
        onSortByMatter={onSortByMatter}
        onSortByTime={onSortByTime}
        showCheckboxes
        mode="deleted"
        sortingMode={sortingMode}
        sortDirection={sortDirection}
        emptyMessage={emptyMessage}
      />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Restore {pendingIds.length} {pendingIds.length === 1 ? "entry" : "entries"}?
            </DialogTitle>
            <DialogDescription>
              {pendingIds.length === 1
                ? "This will move the entry back to your Tracked list."
                : `This will move ${pendingIds.length} deleted entries back to your Tracked list.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isRestoring}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const ids = pendingIds;
                setConfirmOpen(false);
                await performRestore(ids);
                setPendingIds([]);
              }}
              disabled={isRestoring}
              className="bg-[var(--brand-teal)] text-white hover:bg-[var(--brand-teal)]/90"
            >
              {isRestoring ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                "Restore"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default DeletedView;
